// Tiny wrapper around the standard Fullscreen API + a "running as installed
// PWA?" probe. Used by FullscreenPrompt to decide whether to render the
// "Tap to go fullscreen" overlay, and to actually trigger fullscreen on click.
//
// Browsers require a user gesture (click/tap/keypress) for requestFullscreen,
// so this module only exposes pure helpers — the click handler must live
// inside the React event flow.

export function isStandaloneOrFullscreenPwa(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return (
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari uses a non-standard navigator.standalone flag.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export function isFullscreen(): boolean {
  if (typeof document === 'undefined') return false
  // 1. Fullscreen API — set by our own "Go fullscreen" button (and by F11 in a
  //    few browsers that route it through the API).
  if (document.fullscreenElement) return true
  if (typeof window === 'undefined' || !window.matchMedia) return false
  // 2. Browser-native fullscreen (F11) and installed-PWA fullscreen don't
  //    populate document.fullscreenElement, but they do flip the display-mode
  //    media query to `fullscreen`. This is what makes the prompt disappear
  //    when the operator presses F11 instead of tapping our button.
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true
  // 3. Safety net for browsers that hide their chrome on F11 without reporting
  //    display-mode: the viewport ends up filling the whole screen. Allow a few
  //    px of slack for device-pixel-ratio / rounding.
  const { screen } = window
  if (screen && typeof screen.height === 'number' && screen.height > 0) {
    if (
      Math.abs(window.innerHeight - screen.height) <= 2 &&
      Math.abs(window.innerWidth - screen.width) <= 2
    ) {
      return true
    }
  }
  return false
}

export async function enterFullscreen(): Promise<void> {
  const el = document.documentElement
  if (!el.requestFullscreen) return
  try {
    await el.requestFullscreen({ navigationUI: 'hide' })
  } catch {
    // User dismissed the prompt or the browser refused. Silently swallow —
    // the UI will keep showing the prompt for them to try again.
  }
}

/** Subscribe to fullscreen state changes. Returns an unsubscribe fn. */
export function onFullscreenChange(handler: () => void): () => void {
  document.addEventListener('fullscreenchange', handler)
  // Browser-native fullscreen (F11) never fires `fullscreenchange`, so the
  // prompt would otherwise never learn the operator went fullscreen without
  // our button. F11 does resize the viewport and flip the display-mode media
  // query, so track both and let the handler re-read isFullscreen().
  const mql =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(display-mode: fullscreen)')
      : null
  if (typeof window !== 'undefined') window.addEventListener('resize', handler)
  mql?.addEventListener('change', handler)
  return () => {
    document.removeEventListener('fullscreenchange', handler)
    if (typeof window !== 'undefined') window.removeEventListener('resize', handler)
    mql?.removeEventListener('change', handler)
  }
}
